import type { All8PageContent } from "@apa/core-renderers";
import type { ContentDocument } from "../../API/Models";
import { normalizeStorageKey } from "../../Helper/storage";
import type { FieldConfig, Path } from "../../Pages/pageComponentLibrary";
import type { AdminPage, PageMetadata } from "./types";
import type { PageLayoutSettings } from "./pageLayoutSettings";

/** Component shape sent to API / stored in JSON (no key; keys are runtime-only). */
export type PageContentPayloadComponent = { componentType: string; content: unknown };

export type PageContentPayload = {
	slug?: string | null;
	language?: string | null;
	title?: string | null;
	taxonomyIds?: string[] | null;
	dynamicContentTypeId?: string | null;
	dynamicPreviewSlugs?: string[] | null;
	dynamicProductionSlugs?: string[] | null;
	metadata?: PageMetadata | null;
	components?: PageContentPayloadComponent[];
	/** Migration provenance marker — backend-set, round-tripped so a normal
	 *  save never silently drops it; cleared only via the explicit control. */
	source?: string | null;
};

export const safeDecodeSlug = (value?: string | null): string | null | undefined => {
	if (!value) return value;
	try {
		return decodeURIComponent(value);
	} catch {
		return value;
	}
};

export const cloneDeep = <T,>(value: T): T => {
	if (typeof structuredClone === "function") {
		// @ts-ignore - structuredClone might not be in the TS lib target
		return structuredClone(value);
	}
	return JSON.parse(JSON.stringify(value ?? null));
};

const isRecord = (value: unknown): value is Record<string, any> =>
	typeof value === "object" && value !== null && !Array.isArray(value);

export const getValueAtPath = (source: any, path: Path) =>
	path.reduce<any>((acc, key) => {
		if (acc === null || acc === undefined) return undefined;
		return acc[key];
	}, source);

export const setValueAtPath = (source: any, path: Path, value: any) => {
	if (!path.length) return value;
	const clone = cloneDeep(source ?? {});
	let cursor: any = clone;
	for (let i = 0; i < path.length - 1; i++) {
		const key = path[i];
		if (cursor[key] === undefined || cursor[key] === null) {
			cursor[key] = typeof path[i + 1] === "number" ? [] : {};
		}
		cursor = cursor[key];
	}
	cursor[path[path.length - 1]] = value;
	return clone;
};

export const normalizeImageFields = (component: any, fields: FieldConfig[], parentPath: Path = []) => {
	let updated = component;
	fields.forEach((field) => {
		const fullPath = [...parentPath, ...field.path];
		if (field.type === "image") {
			const current = getValueAtPath(updated, fullPath);
			if (typeof current === "string") {
				const normalized = normalizeStorageKey(current);
				if (normalized !== current) {
					updated = setValueAtPath(updated, fullPath, normalized);
				}
			} else if (current && typeof current === "object" && typeof current.src === "string") {
				const normalized = normalizeStorageKey(current.src);
				if (normalized !== current.src) {
					updated = setValueAtPath(updated, fullPath, { ...current, src: normalized });
				}
			}
			return;
		}

		if (field.type === "object") {
			updated = normalizeImageFields(updated, field.fields, fullPath);
			return;
		}

		if (field.type === "array" && field.itemType === "object" && field.fields) {
			const items = getValueAtPath(updated, fullPath);
			if (Array.isArray(items)) {
				items.forEach((_, index) => {
					updated = normalizeImageFields(updated, field.fields ?? [], [...fullPath, index]);
				});
			}
		}
	});
	return updated;
};

/** Generate a stable runtime key for a component (not persisted). */
export const getComponentRuntimeKey = (componentType: string, index: number): string =>
	`${componentType || "component"}-${index}`;

/** Strip key from content object so it is never stored in JSON/MD. */
function stripKeyFromContent(content: unknown): unknown {
	if (content === null || typeof content !== "object" || Array.isArray(content)) return content;
	const { key: _k, ...rest } = content as Record<string, unknown>;
	return rest;
}

/** Components without key, for API payload and storage. Keys are generated at runtime only. */
export const componentsForPayload = (components: All8PageContent): PageContentPayloadComponent[] =>
	(components ?? []).map((c) => ({
		componentType: (c?.componentType ?? "").toString(),
		content: stripKeyFromContent(c?.content ?? {})
	}));

export const normalizeComponents = (components?: any[] | null): All8PageContent => {
	if (!Array.isArray(components)) return [];
	return components.map((component, index) => {
		const componentType = (component?.componentType ?? component?.ComponentType ?? "").toString();
		const key = getComponentRuntimeKey(componentType, index);
		const rawContent = component?.content ?? component?.Content ?? {};
		const content = stripKeyFromContent(rawContent) as Record<string, unknown>;
		return { componentType, key, content: Object.keys(content).length ? content : {} };
	});
};

export const attachComponents = (draft: AdminPage | null): AdminPage | null => {
	if (!draft) return draft;
	return { ...draft, components: normalizeComponents(draft.components) };
};

export const keywordsToInput = (keywords?: string[] | null) => (keywords ?? []).join(", ");
export const inputToKeywords = (value: string) =>
	value
		.split(",")
		.map((entry) => entry.trim())
		.filter(Boolean);

export const buildPagePayload = (draft: AdminPage | null, fallbackLanguage: string): PageContentPayload => ({
	slug: draft?.slug ?? "",
	language: draft?.language ?? fallbackLanguage,
	title: draft?.title ?? "",
	taxonomyIds: Array.isArray(draft?.taxonomyIds) ? draft?.taxonomyIds ?? [] : [],
	dynamicContentTypeId: draft?.dynamicContentTypeId ?? null,
	dynamicPreviewSlugs: Array.isArray(draft?.dynamicPreviewSlugs) ? draft?.dynamicPreviewSlugs ?? [] : [],
	dynamicProductionSlugs: Array.isArray(draft?.dynamicProductionSlugs) ? draft?.dynamicProductionSlugs ?? [] : [],
	metadata: (draft?.metadata ?? {}) as PageMetadata,
	components: componentsForPayload(normalizeComponents(draft?.components)),
	// Round-trip the migration marker so an ordinary save preserves it; it is
	// dropped only when the editor explicitly clears draft.source (the
	// "Remove migrated tag" control).
	source: draft?.source ?? null
});

export const serializePagePayload = (draft: AdminPage | null, fallbackLanguage: string) =>
	JSON.stringify(buildPagePayload(draft, fallbackLanguage), null, 2);

/** Stable string of slug, language, title, metadata, components for comparing draft vs last-deployed preview. */
export const previewFingerprint = (draft: AdminPage | null, fallbackLanguage: string): string =>
	draft ? JSON.stringify(buildPagePayload(draft, fallbackLanguage)) : "";

export const parsePagePayload = (raw: string, base: AdminPage | null, fallbackLanguage: string): AdminPage => {
	const parsed = JSON.parse(raw);
	if (!isRecord(parsed)) {
		throw new Error("JSON not valid.");
	}
	const payload = parsed as Record<string, any>;
	const fallback: AdminPage = base ?? {
		slug: "",
		language: fallbackLanguage,
		title: "",
		taxonomyIds: [],
		dynamicContentTypeId: null,
		dynamicPreviewSlugs: [],
		dynamicProductionSlugs: [],
		metadata: {},
		components: []
	};
	const nextComponents = Array.isArray(payload.components)
		? normalizeComponents(payload.components)
		: normalizeComponents(fallback.components);
	const nextMetadata = isRecord(payload.metadata) ? (payload.metadata as PageMetadata) : fallback.metadata ?? {};
	const nextSlug = typeof payload.slug === "string" ? payload.slug : fallback.slug ?? "";
	const nextLanguage =
		typeof payload.language === "string" ? payload.language : fallback.language ?? fallbackLanguage;
	const nextTitle = typeof payload.title === "string" ? payload.title : fallback.title ?? "";
	const nextTaxonomyIds = Array.isArray(payload.taxonomyIds)
		? payload.taxonomyIds.filter(Boolean)
		: fallback.taxonomyIds ?? [];
	const nextDynamicContentTypeId =
		typeof payload.dynamicContentTypeId === "string" ? payload.dynamicContentTypeId : fallback.dynamicContentTypeId;
	const nextDynamicPreviewSlugs = Array.isArray(payload.dynamicPreviewSlugs)
		? payload.dynamicPreviewSlugs.filter(Boolean)
		: fallback.dynamicPreviewSlugs ?? [];
	const nextDynamicProductionSlugs = Array.isArray(payload.dynamicProductionSlugs)
		? payload.dynamicProductionSlugs.filter(Boolean)
		: fallback.dynamicProductionSlugs ?? [];
	return {
		...fallback,
		slug: nextSlug,
		language: nextLanguage,
		title: nextTitle,
		taxonomyIds: nextTaxonomyIds,
		dynamicContentTypeId: nextDynamicContentTypeId ?? null,
		dynamicPreviewSlugs: nextDynamicPreviewSlugs,
		dynamicProductionSlugs: nextDynamicProductionSlugs,
		metadata: nextMetadata,
		components: nextComponents
	};
};

export const buildDraftFromContentDoc = (
	doc: ContentDocument | undefined,
	fallbackLanguage: string,
	defaultLanguage: string
): AdminPage | null => {
	if (!doc) return null;
	const data = (doc.data ?? {}) as any;
	const rawSlug = (data?.slug as string | undefined) ?? "";
	const trimmedSlug = rawSlug.trim();
	if (!trimmedSlug) {
		return null;
	}

	const decodedSlug = safeDecodeSlug(trimmedSlug) ?? trimmedSlug;
	const language = (
		(data?.language as string | undefined) ??
		doc.language ??
		fallbackLanguage ??
		defaultLanguage
	).toLowerCase();

	const titleFromData = (data?.title as string | undefined)?.trim();
	const metadataFromData = (data?.metadata as PageMetadata | undefined) ?? {};
	const title =
		titleFromData ||
		(metadataFromData.title as string | null | undefined)?.toString() ||
		decodedSlug;

	const metadata: PageMetadata = {
		...metadataFromData,
		title: (metadataFromData.title as string | undefined) ?? "",
		description: (metadataFromData.description as string | undefined) ?? ""
	};

	const components = normalizeComponents((data?.components as any[] | undefined) ?? []);
	const taxonomyIds = Array.isArray(data?.taxonomyIds)
		? (data?.taxonomyIds as string[]).filter(Boolean)
		: [];
	const dynamicPreviewSlugs = Array.isArray(data?.dynamicPreviewSlugs)
		? (data?.dynamicPreviewSlugs as string[]).filter(Boolean)
		: [];
	const dynamicProductionSlugs = Array.isArray(data?.dynamicProductionSlugs)
		? (data?.dynamicProductionSlugs as string[]).filter(Boolean)
		: [];
	const dynamicContentTypeId =
		typeof data?.dynamicContentTypeId === "string" ? (data.dynamicContentTypeId as string) : null;
	const layout = readLayoutFromData(data);

	return {
		id: doc.id ?? null,
		slug: decodedSlug,
		language,
		title,
		taxonomyIds,
		dynamicContentTypeId,
		dynamicPreviewSlugs,
		dynamicProductionSlugs,
		metadata,
		layout,
		components,
		source: typeof data?.source === "string" && data.source.trim() ? data.source.trim() : null,
		isDeleted: doc.isDeleted ?? false,
		previewDeployedAt: doc.previewDeployedAt ?? null,
		productionDeployedAt: doc.productionDeployedAt ?? null
	};
};

/** Coerces the raw `layout` blob from API/MinIO into a typed PageLayoutSettings.
 *  Tolerant of unknown extra keys and missing fields — drops anything we don't
 *  recognize so the in-memory shape stays clean. Returns null when nothing
 *  meaningful is set so the caller can omit the field from payloads. */
function readLayoutFromData(data: any): PageLayoutSettings | null {
	const raw = data?.layout;
	if (!raw || typeof raw !== "object") return null;
	const out: PageLayoutSettings = {};
	if (raw.mode === "fluid" || raw.mode === "constrained") out.mode = raw.mode;
	if (raw.firstWidgetMobileFlush === true) out.firstWidgetMobileFlush = true;
	if (raw.lastWidgetFlushBottom === true) out.lastWidgetFlushBottom = true;
	return Object.keys(out).length > 0 ? out : null;
}
